import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import type { ExtensionListItem } from '@kirin/api-client/contracts/extensions';
import { PERSONAL_EXTENSION_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/account-extensions';
import { severityOptionsDescending } from '@kirin/api-client/contracts/shared/severity';
import {
  NameCell,
  PublisherCell,
  IDECell,
  FirstSeenCell,
  LastSeenCell,
  ScanResultLinkCell,
} from './CellRenderers';
import { SourceCell } from '@/components/shared/extensions';
import { SecuritySummaryCell } from '@/components/shared/extensions/SecuritySummaryCell';
import { RiskEstimateCell } from '@/components/shared/extensions/RiskEstimateCell';
import {
  ExtensionSourceFilterOption,
  ExtensionIDEFilterOption,
  RiskEstimateFilterOption,
} from './FilterComponents';
import { SeverityFilterOption } from '@/components/DataTable/alerts/FilterComponents';
import {
  EXTENSION_FILTER_URL_KEYS,
  EXTENSION_FILTER_OPTIONS_PAGE_SIZE,
  EXTENSION_SOURCE_OPTIONS,
} from './constants';
import {
  fetchExtensionFilterOptions,
  getExtensionFilterOptionsQueryKey,
} from './api';

const EmptyOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
  ...EmptyOperators,
];

const StringOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
  ...EmptyOperators,
];

const EnumOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  ...EmptyOperators,
];

/**
 * Column definitions for the personal extensions DataTable.
 *
 * The IDE filter is server-backed and reflects the user's actual inventory
 * (KIR-2730) — the previous static, `releasedPlatforms`-gated list showed
 * IDEs the user did not have extensions from.
 *
 * Column order: Name, Publisher, IDE, Source, Risk, First Seen, Last Seen
 * (+ scan-result link when capability_detection is on).
 */
export function createExtensionColumns(
  capabilityDetectionEnabled: boolean = false,
  agentmeshRiskEnabled: boolean = false
): ColumnConfig<ExtensionListItem>[] {
  const securityColumn: ColumnConfig<ExtensionListItem> = {
    id: PERSONAL_EXTENSION_FILTERABLE_FIELDS.SECURITY,
    header: 'Risk',
    accessor: 'securitySummary',
    width: 150,
    renderCell: ({ row }) => (
      <SecuritySummaryCell summary={row.securitySummary} />
    ),
    sortable: true,
    sortField: 'security',
    sortKey: 'sec',
    filterable: true,
    dataType: ColumnDataTypes.ENUM,
    filterOperators: EnumOperators,
    statePersistence: {
      urlKey:
        EXTENSION_FILTER_URL_KEYS[
          PERSONAL_EXTENSION_FILTERABLE_FIELDS.SECURITY
        ],
    },
    filterOptions: {
      type: FilterOptionTypes.STATIC,
      options: severityOptionsDescending,
      render: (option, isSelected) => (
        <SeverityFilterOption severity={option.value} isSelected={isSelected} />
      ),
    },
  };

  // Risk Estimate (agentmesh_risk_estimate, KIR-1928): the overall AgentMesh
  // score, styled like the Alerts severity column. Multiselect filter options
  // are server-backed (distinct labels present in the user's inventory) so
  // AgentMesh vocabulary changes flow through automatically.
  const riskEstimateColumn: ColumnConfig<ExtensionListItem> = {
    id: PERSONAL_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE,
    header: 'Risk',
    accessor: 'riskEstimate',
    width: 150,
    renderCell: ({ row }) => (
      <RiskEstimateCell riskEstimate={row.riskEstimate} />
    ),
    sortable: true,
    sortField: 'riskEstimate',
    sortKey: 'risk',
    filterable: true,
    dataType: ColumnDataTypes.ENUM,
    filterOperators: EnumOperators,
    statePersistence: {
      urlKey:
        EXTENSION_FILTER_URL_KEYS[
          PERSONAL_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE
        ],
    },
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: params =>
        getExtensionFilterOptionsQueryKey(
          PERSONAL_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE,
          { search: params.search, limit: params.limit }
        ),
      queryFn: params =>
        fetchExtensionFilterOptions(
          PERSONAL_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE,
          params
        ),
      queryConfig: { staleTime: 2 * 60 * 1000 },
      pagination: {
        mode: 'offset',
        limit: EXTENSION_FILTER_OPTIONS_PAGE_SIZE,
      },
      render: (option, isSelected) => (
        <RiskEstimateFilterOption
          value={option.value}
          isSelected={isSelected}
        />
      ),
    },
  };

  return [
    {
      id: PERSONAL_EXTENSION_FILTERABLE_FIELDS.NAME,
      header: 'Name',
      accessor: 'extensionName',
      width: 280,
      renderCell: ({ row }) => <NameCell row={row} />,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey:
          EXTENSION_FILTER_URL_KEYS[PERSONAL_EXTENSION_FILTERABLE_FIELDS.NAME],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getExtensionFilterOptionsQueryKey(
            PERSONAL_EXTENSION_FILTERABLE_FIELDS.NAME,
            {
              search: params.search,
              limit: params.limit,
            }
          ),
        queryFn: params =>
          fetchExtensionFilterOptions(
            PERSONAL_EXTENSION_FILTERABLE_FIELDS.NAME,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        pagination: {
          mode: 'offset',
          limit: EXTENSION_FILTER_OPTIONS_PAGE_SIZE,
        },
      },
    },
    {
      id: PERSONAL_EXTENSION_FILTERABLE_FIELDS.PUBLISHER,
      header: 'Publisher',
      accessor: 'publisher',
      width: 160,
      renderCell: ({ row }) => <PublisherCell row={row} />,
      sortable: true,
      sortField: 'publisher',
      sortKey: 'pub',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey:
          EXTENSION_FILTER_URL_KEYS[
            PERSONAL_EXTENSION_FILTERABLE_FIELDS.PUBLISHER
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getExtensionFilterOptionsQueryKey(
            PERSONAL_EXTENSION_FILTERABLE_FIELDS.PUBLISHER,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchExtensionFilterOptions(
            PERSONAL_EXTENSION_FILTERABLE_FIELDS.PUBLISHER,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        pagination: {
          mode: 'offset',
          limit: EXTENSION_FILTER_OPTIONS_PAGE_SIZE,
        },
      },
    },
    {
      id: PERSONAL_EXTENSION_FILTERABLE_FIELDS.IDE,
      header: 'IDE',
      accessor: 'sources',
      width: 130,
      renderCell: ({ row }) => <IDECell row={row} />,
      sortable: true,
      sortField: 'ide',
      sortKey: 'ide',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey:
          EXTENSION_FILTER_URL_KEYS[PERSONAL_EXTENSION_FILTERABLE_FIELDS.IDE],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getExtensionFilterOptionsQueryKey(
            PERSONAL_EXTENSION_FILTERABLE_FIELDS.IDE,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchExtensionFilterOptions(
            PERSONAL_EXTENSION_FILTERABLE_FIELDS.IDE,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        pagination: {
          mode: 'offset',
          limit: EXTENSION_FILTER_OPTIONS_PAGE_SIZE,
        },
        render: (option, isSelected) => (
          <ExtensionIDEFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: PERSONAL_EXTENSION_FILTERABLE_FIELDS.SOURCE,
      header: 'Source',
      accessor: 'isBuiltIn',
      width: 110,
      renderCell: ({ row }) => (
        <SourceCell source={row.isBuiltIn ? 'built-in' : 'added'} />
      ),
      sortable: true,
      sortField: 'source',
      sortKey: 'src',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey:
          EXTENSION_FILTER_URL_KEYS[
            PERSONAL_EXTENSION_FILTERABLE_FIELDS.SOURCE
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: EXTENSION_SOURCE_OPTIONS,
        render: (option, isSelected) => (
          <ExtensionSourceFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    // Risk — while agentmesh_risk_estimate is on, the AgentMesh estimate
    // REPLACES the capability-detection severity in this slot (KIR-1928).
    ...(agentmeshRiskEnabled
      ? [riskEstimateColumn]
      : capabilityDetectionEnabled
        ? [securityColumn]
        : []),
    {
      id: PERSONAL_EXTENSION_FILTERABLE_FIELDS.FIRST_SEEN_AT,
      header: 'First Seen',
      accessor: 'firstSeenAt',
      width: 130,
      renderCell: ({ row }) => <FirstSeenCell row={row} />,
      sortable: true,
      sortField: 'firstSeenAt',
      sortKey: 'fs',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          EXTENSION_FILTER_URL_KEYS[
            PERSONAL_EXTENSION_FILTERABLE_FIELDS.FIRST_SEEN_AT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: PERSONAL_EXTENSION_FILTERABLE_FIELDS.LAST_SEEN_AT,
      header: 'Last Seen',
      accessor: 'lastSeenAt',
      width: 130,
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      sortable: true,
      sortField: 'lastSeenAt',
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          EXTENSION_FILTER_URL_KEYS[
            PERSONAL_EXTENSION_FILTERABLE_FIELDS.LAST_SEEN_AT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    ...(capabilityDetectionEnabled
      ? ([
          {
            id: 'scanResult',
            header: '',
            accessor: 'extensionId',
            width: 150,
            renderCell: ({ row }) => <ScanResultLinkCell row={row} />,
            sortable: false,
            filterable: false,
          } satisfies ColumnConfig<ExtensionListItem>,
        ] as ColumnConfig<ExtensionListItem>[])
      : []),
  ];
}
